// Service Checklist Types

import { DropdownOption } from "../../components/NewInputFields/DropdownInput";
import { Header } from "../../components/Tables/types";
import Strings from "../../values/strings";
import { DeletedQuestion } from "./ServiceChecklists/Components/types";
import { ServiceResultText, toolServiceStatuses } from "./ServiceReports/types";

// Table headers
export const SCHEDULING_HEADERS: Header[] = [
  { label: Strings.servicing.nfcCode, key: "nfcCode" },
  { label: Strings.servicing.assetCategory, key: "assetCategory" },
  { label: Strings.servicing.scheduled, key: "scheduled" },
  { label: Strings.servicing.status, key: "status" },
  { label: Strings.servicing.prevServiceResults, key: "prevServiceResults" },
  { label: Strings.servicing.details, key: "details" },
  { label: Strings.servicing.actions, key: "actionMenu" },
];

export const SERVICE_CHECKLISTS_HEADERS: Header[] = [
  { label: Strings.servicing.title, key: "title" },
  { label: Strings.servicing.type, key: "serviceType" },
  { label: Strings.servicing.assetCategory, key: "assetCategory" },
  { label: Strings.servicing.status, key: "status" },
  { label: Strings.servicing.addedBy, key: "addedBy" },
  { label: Strings.servicing.actions, key: "actionMenu" },
];

export const SERVICE_REPORTS_HEADERS: Header[] = [
  { label: Strings.servicing.nfcCode, key: "nfcCode" },
  { label: Strings.servicing.assetCategory, key: "assetCategory" },
  { label: Strings.servicing.title, key: "title" },
  { label: Strings.servicing.type, key: "type" },
  { label: Strings.servicing.resultAndAssetCondition, key: "resultAndCondition" },
  { label: Strings.servicing.servicer, key: "servicer" },
  { label: Strings.servicing.conductedOn, key: "conductedOn" },
  { label: Strings.servicing.actions, key: "actionMenu" },
];
export const SERVICE_REPORTS_HEADERS_FOR_A_SINGLE_ASSET: Header[] = [
  { label: Strings.servicing.title, key: "title" },
  { label: Strings.servicing.type, key: "type" },
  { label: Strings.servicing.resultAndAssetCondition, key: "resultAndCondition" },
  { label: Strings.servicing.servicer, key: "servicer" },
  { label: Strings.servicing.conductedOn, key: "conductedOn" },
  { label: Strings.servicing.actions, key: "actionMenu" },
];

export type ServiceChecklist = {
  _id: string;
  status: number; // 0 = Draft, 1 = Published
  serviceType: string;
  assetCategory: string; // This is an ID, not a name
  title: string;
  preServiceInstructions?: string;
  failedServiceInstructionsOperable?: string;
  failedServiceInstructionsOutOfOrder?: string;
  checklists: ChecklistItem[];
  organisationId: string;
  subOrganisationId?: string | null;
  addedBy: AddedByUser;
  updatedBy: string;
  addedAt: string; // ISO date string
  updatedAt: string; // ISO date string
  __v: number;
};

export type ChecklistItem = {
  order: number;
  question: string;
  answerType: "YesNoNotApplicable" | "PassFailNotApplicable";
  textInputRequired: boolean;
  addPhotos: boolean;
  addNotes: boolean;
  assetConditionRequired: boolean;
};

export type AddedByUser = {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
};

export enum ANSWER_TYPE {
  YesNoNotApplicable = "YesNoNotApplicable",
  PassFailNotApplicable = "PassFailNotApplicable",
}

// Menu Action Enum
export enum MenuAction {
  EDIT = "edit",
  DUPLICATE = "duplicate",
  DELETE = "delete",
  REARRANGE = "rearrange",
  HISTORY = "history",
}

// Status Enums
export enum StatusText {
  DRAFT = "Draft",
  PUBLISHED = "Published",
}

export enum Status {
  DRAFT = 0,
  PUBLISHED = 1,
}

 // Service Status configuration
 export const SERVICE_STATUS_CONFIG = {
  [ServiceResultText.PASSED]: '#10b981',
  [ServiceResultText.FAILED_OUT_OF_ORDER]: '#ef4444',
  [ServiceResultText.FAILED_OPERABLE]: '#f59e0b',
  [ServiceResultText.NOT_SERVICED]: '#6b7280',
  [ServiceResultText.FAILED]: '#ef4444',
};

 // SchedulingStatus configuration
 export const SCHEDULING_STATUS_CONFIG = {
  [toolServiceStatuses.DUESOON]: '#357DE5',
  [toolServiceStatuses.SCHEDULED]: '#38C39D',
  [toolServiceStatuses.OVERDUE]: '#D9594E',
  [toolServiceStatuses.UNSCHEDULED]: '#A8A8A8',
};

export enum StatusColor {
  DRAFT = "#FF4444",
  PUBLISHED = "#00C851",
}

export type FilterQuery = {
  page?: number;
  limit?: number;
  title?: string;
  status?: string[];
  serviceType?: string[];
  assetCategory?: string[];
  addedBy?: string;
};

export enum ServiceType {
  DAILY = "Daily Inspection",
  WEEKLY = "Weekly Inspection",
  MONTHLY = "Monthly Inspection",
  QUARTERLY = "3 Monthly Inspection",
  HALF_YEARLY = "6 Monthly Inspection",
  YEARLY = "Annual Inspection",
  NON_RECURRING = "Non-Recurring Service",
}

export enum TabNames {
  SCHEDULING = "Scheduling",
  REPORTS = "Reports",
  SERVICE_CHECKLISTS = "Service Checklists",
  SERVICING_DETAILS = "Servicing Details",
  HISTORY = "History",
}

export enum FilterLabels {
  ASSET_CATEGORY = "Asset category",
  SCHEDULING = "Scheduling",
  SERVICE_STATUS = "Service status",
  SCHEDULED_FOR = "Scheduled For",
  TYPE = "Type",
  RESULT_AND_ASSET_CONDITION = "Result & Asset Condition",
  CONDUCTED_ON = "Conducted On",
  STATUS = "Status",
}

export const answerTypeOptions: DropdownOption[] = [
  { label: "Yes, No, Not Applicable", value: "YesNoNotApplicable" },
  { label: "Pass, Fail, Not Applicable", value: "PassFailNotApplicable" },
];

// Checklist option types
export type ChecklistOption =
  | "textInputRequired"
  | "addPhotos"
  | "addNotes"
  | "assetConditionRequired";

// Answer type
export type AnswerType = "YesNoNotApplicable" | "PassFailNotApplicable";

export type ChangeField = {
  oldValue: string | boolean | number;
  newValue: string | boolean | number;
};

export type ChangeItem = {
  order: number;
  question?: ChangeField;
  answerType?: ChangeField;
  textInputRequired?: ChangeField;
  addPhotos?: ChangeField;
  addNotes?: ChangeField;
  assetConditionRequired?: ChangeField;
};

export type DeletedItem = {
  order: number;
  question: string;
  answerType: string;
  textInputRequired: boolean;
  addPhotos: boolean;
  addNotes: boolean;
  assetConditionRequired: boolean;
};

export type HistoryEntry = {
  templateId: string;
  timestamp: string;
  editedBy?: string;
  createdBy?: string;
  changes: ChangeItem[];
  deletedQuestions?: DeletedQuestion[];
  changeType: string;
};

// Scheduling Types
export type ServiceSchedule = {
  scheduleId: string;
  title: string;
  type: string;
  lastServicedDate?: string;
  nextServicingDate: string;
  serviceReminderDate?: string;
  _id: string;
  schedulingStatus: string;
  autoRescheduling?: boolean;
  lastServiceResult: number;
};

export type SchedulingTool = {
  _id: string;
  serialNumber: string;
  toolId: string;
  serviceSchedules: ServiceSchedule[];
  nfcCode: string | null;
  toolName: string;
  brand?: string;
  modelNo?: string;
  fixedAssetNumber?: string;
  serialNumberForClient?: string;
  replacementValue?: number | null;
  warrantyExpiryDate?: string | null;
  addedBy?: string;
  updatedBy?: string | null;
  linkedDevice?: string | null;
  accessories?: any[];
  attachments?: any[];
  addedAt?: string;
  updatedAt?: string;
  lastServicedOn?: string;
  serviceDueDate?: string;
  __v?: number;
  serviceType?: ServiceType;
};

export type SchedulingFilters = {
  searchText?: string;
  serviceStatus?: number | number[];
  schedulingStatus?: string | string[];
  toolName?: string | string[];
  scheduledServiceDateRange?: { from?: string; to?: string };
};
